import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position
} from "reactflow";
import "reactflow/dist/style.css";
import {
  ArrowLeft,
  Bookmark,
  Share2,
  Sparkles,
  Briefcase,
  GraduationCap,
  School,
  Baby,
  Lightbulb,
  AlertTriangle,
  Target,
  Brain,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Loader2,
  Maximize2,
  Minimize2
} from "lucide-react";
import { ModeToggle } from "@/components/mode-toggle";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Custom node component for React Flow
const CustomNode = ({ data }) => {
  return (
    <div className="custom-diagram-node">
      <Handle type="target" position={Position.Top} className="!bg-primary/50 !w-2 !h-2" />
      <span className="text-sm font-medium">{data.label}</span>
      <Handle type="source" position={Position.Bottom} className="!bg-primary/50 !w-2 !h-2" />
    </div>
  );
};

const nodeTypes = { custom: CustomNode };

const lensConfig = {
  Professional: { icon: Briefcase, color: "lens-professional", bgColor: "lens-professional-bg" },
  Student: { icon: GraduationCap, color: "lens-student", bgColor: "lens-student-bg" },
  "High School": { icon: School, color: "lens-highschool", bgColor: "lens-highschool-bg" },
  Kid: { icon: Baby, color: "lens-kid", bgColor: "lens-kid-bg" },
  Synthesis: { icon: Sparkles, color: "lens-synthesis", bgColor: "lens-synthesis-bg" },
};

const quickActions = [
  { label: "Simpler", action: "simpler" },
  { label: "More technical", action: "technical" },
  { label: "More examples", action: "examples" },
  { label: "Shorter", action: "shorter" },
  { label: "Different analogy", action: "analogy" },
];

export default function TopicPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refining, setRefining] = useState(false);
  const [activeLens, setActiveLens] = useState("Professional");
  const [activeDiagram, setActiveDiagram] = useState(0);
  const [isSaved, setIsSaved] = useState(false);

  // Quiz state
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [teachBack, setTeachBack] = useState("");

  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Fetch topic data
  useEffect(() => {
    const fetchTopic = async () => {
      try {
        const response = await fetch(`${API}/topics/${id}`);
        if (!response.ok) throw new Error("Topic not found");
        const data = await response.json();
        setTopic(data);
        setIsSaved(data.saved);

        // Initialize diagram if available
        if (data.diagrams && data.diagrams.length > 0) {
          updateDiagram(data.diagrams[0]);
        }
      } catch (error) {
        toast.error("Failed to load topic");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    fetchTopic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Update diagram nodes and edges
  const updateDiagram = useCallback((diagram) => {
    if (!diagram) return;

    const nodePositions = {};
    const numNodes = diagram.nodes?.length || 0;
    const radius = 280; // Increased for better spacing
    const centerX = 400;
    const centerY = 300;

    // Position nodes in a circle for concept maps, or grid for flowcharts
    const flowNodes = (diagram.nodes || []).map((node, index) => {
      let x, y;
      if (diagram.type === "flowchart" || diagram.type === "timeline") {
        // Wider grid spacing
        x = 100 + (index % 3) * 320;
        y = 100 + Math.floor(index / 3) * 200;
      } else {
        // Larger circle with more spacing
        const angle = (2 * Math.PI * index) / numNodes - Math.PI / 2;
        x = centerX + radius * Math.cos(angle);
        y = centerY + radius * Math.sin(angle);
      }

      nodePositions[node.id] = { x, y };

      return {
        id: node.id,
        type: "custom",
        position: { x, y },
        data: { label: node.label },
      };
    });

    const flowEdges = (diagram.edges || []).map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label,
      type: "smoothstep",
      animated: true,
      style: { stroke: "hsl(var(--muted-foreground))" },
      markerEnd: { type: MarkerType.ArrowClosed, color: "hsl(var(--muted-foreground))" },
      labelStyle: { fill: "hsl(var(--foreground))", fontSize: 12 },
      labelBgStyle: { fill: "hsl(var(--card))" },
    }));

    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [setNodes, setEdges]);

  // Handle diagram switch
  const handleDiagramSwitch = (index) => {
    setActiveDiagram(index);
    if (topic?.diagrams?.[index]) {
      updateDiagram(topic.diagrams[index]);
    }
  };

  // Handle save
  const handleSave = async () => {
    try {
      await fetch(`${API}/topics/${id}/save`, { method: "POST" });
      setIsSaved(true);
      toast.success("Saved to your library!");
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  // Handle quick action (refine synthesis)
  const handleQuickAction = async (action) => {
    if (refining) return;
    setRefining(true);
    try {
      const response = await fetch(`${API}/topics/${id}/refine`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ modifier: action }),
      });

      if (!response.ok) throw new Error("Refine failed");

      const data = await response.json();
      if (data.success && data.synthesis) {
        setTopic((prev) => ({ ...prev, synthesis: data.synthesis }));
        toast.success(`Updated: ${action} version`);
        // Switch to Synthesis tab to show the update
        setActiveLens("Synthesis");
      }
    } catch (error) {
      toast.error("Failed to refine. Please try again.");
      console.error(error);
    } finally {
      setRefining(false);
    }
  };

  // Handle quiz answer
  const handleQuizAnswer = (questionId, answer) => {
    setQuizAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  // Check quiz results
  const checkQuiz = () => {
    setShowResults(true);
    const questions = topic?.quiz?.questions || [];
    const correct = questions.filter((q) => quizAnswers[q.id] === q.correct_answer).length;
    toast.success(`You got ${correct}/${questions.length} correct!`);
  };

  // Get current lens content
  const currentLens = useMemo(() => {
    if (!topic?.lenses) return null;
    if (activeLens === "Synthesis") return null;
    return topic.lenses.find((l) => l.perspective === activeLens);
  }, [topic, activeLens]);

  if (loading) {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <Skeleton className="h-12 w-48 skeleton-shimmer" />
          <Skeleton className="h-24 w-full skeleton-shimmer" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Skeleton className="h-48 skeleton-shimmer" />
            <Skeleton className="h-48 skeleton-shimmer" />
            <Skeleton className="h-48 skeleton-shimmer" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 glass py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>

          <div className="flex items-center gap-2">
            <ModeToggle />
            <Button
              variant="ghost"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                toast.success("Link copied!");
              }}
              className="gap-2"
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaved}
              variant={isSaved ? "secondary" : "default"}
              className={`gap-2 rounded-full ${isSaved ? "bg-green-100 text-green-700 hover:bg-green-200 border-none" : ""}`}
              data-testid="save-btn"
            >
              <Bookmark className="w-4 h-4" />
              {isSaved ? "Saved" : "Save"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 pt-8 space-y-10">
        {/* Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <Badge variant="outline" className="mb-4 text-primary border-primary">
            {topic?.classifier?.topic_type || "Concept"}
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="topic-summary">
            {topic?.summary || "Understanding your topic"}
          </h1>
          {topic?.classifier?.tags && (
            <div className="flex justify-center gap-2 flex-wrap">
              {topic.classifier.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="bg-muted/50">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </motion.div>

        {/* Lens Stack */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Tabs value={activeLens} onValueChange={setActiveLens}>
            <TabsList className="glass rounded-full p-1 w-fit mx-auto mb-6 flex-wrap justify-center" data-testid="lens-tabs">
              {Object.entries(lensConfig).map(([name, config]) => {
                const Icon = config.icon;
                return (
                  <TabsTrigger
                    key={name}
                    value={name}
                    className={`rounded-full px-4 py-2 gap-2 data-[state=active]:${config.bgColor}`}
                    data-testid={`lens-tab-${name.toLowerCase().replace(" ", "-")}`}
                  >
                    <Icon className={`w-4 h-4 ${config.color}`} />
                    <span className="hidden sm:inline">{name}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {/* Lens Content */}
            {Object.keys(lensConfig).map((name) => (
              <TabsContent key={name} value={name} className="mt-0">
                <AnimatePresence mode="wait">
                  {name === "Synthesis" ? (
                    <SynthesisCard synthesis={topic?.synthesis} />
                  ) : (
                    <LensCard lens={topic?.lenses?.find((l) => l.perspective === name)} name={name} />
                  )}
                </AnimatePresence>
              </TabsContent>
            ))}
          </Tabs>
        </motion.section>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex justify-center gap-2 flex-wrap"
        >
          <span className="text-sm text-muted-foreground mr-2 self-center">Make it:</span>
          {quickActions.map((action) => (
            <button
              key={action.action}
              onClick={() => handleQuickAction(action.action)}
              disabled={refining}
              className={`quick-action ${refining ? 'opacity-50 cursor-wait' : ''}`}
              data-testid={`quick-action-${action.action}`}
            >
              {refining ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
              {action.label}
            </button>
          ))}
        </motion.div>

        {/* Visual Maps */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Visual Maps</h2>
            <div className="flex gap-2">
              {topic?.diagrams && topic.diagrams.length > 1 && (
                <>
                  {topic.diagrams.map((d, i) => (
                    <Button
                      key={i}
                      variant={activeDiagram === i ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleDiagramSwitch(i)}
                      className={activeDiagram === i ? "bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary" : ""}
                      data-testid={`diagram-tab-${i}`}
                    >
                      {d.type?.replace("-", " ") || `Diagram ${i + 1}`}
                    </Button>
                  ))}
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsFullscreen(true)}
                className="gap-1"
                data-testid="fullscreen-btn"
              >
                <Maximize2 className="w-4 h-4" />
                Fullscreen
              </Button>
            </div>
          </div>

          <Card className="card-base overflow-hidden" data-testid="diagram-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">
                {topic?.diagrams?.[activeDiagram]?.title || "Concept Visualization"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="diagram-container">
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  nodeTypes={nodeTypes}
                  fitView
                  fitViewOptions={{ padding: 0.3 }}
                  minZoom={0.3}
                  maxZoom={2}
                  proOptions={{ hideAttribution: true }}
                >
                  <Background color="hsl(var(--muted-foreground))" gap={20} size={1} />
                  <Controls />
                </ReactFlow>
              </div>
              {topic?.diagrams?.[activeDiagram]?.caption && (
                <p className="mt-4 text-sm text-muted-foreground text-center italic">
                  {topic.diagrams[activeDiagram].caption}
                </p>
              )}
            </CardContent>
          </Card>
        </motion.section>

        {/* Fullscreen Diagram Modal */}
        <AnimatePresence>
          {isFullscreen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-background"
            >
              <div className="absolute top-4 right-4 z-10 flex gap-2">
                {topic?.diagrams && topic.diagrams.length > 1 && (
                  <>
                    {topic.diagrams.map((d, i) => (
                      <Button
                        key={i}
                        variant={activeDiagram === i ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleDiagramSwitch(i)}
                        className={activeDiagram === i ? "bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary" : ""}
                      >
                        {d.type?.replace("-", " ") || `Diagram ${i + 1}`}
                      </Button>
                    ))}
                  </>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsFullscreen(false)}
                  className="gap-1"
                >
                  <Minimize2 className="w-4 h-4" />
                  Exit
                </Button>
              </div>
              <div className="absolute top-4 left-4 z-10">
                <h2 className="text-xl font-bold">
                  {topic?.diagrams?.[activeDiagram]?.title || "Concept Visualization"}
                </h2>
              </div>
              <div className="w-full h-full">
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  nodeTypes={nodeTypes}
                  fitView
                  fitViewOptions={{ padding: 0.2 }}
                  minZoom={0.2}
                  maxZoom={3}
                  proOptions={{ hideAttribution: true }}
                >
                  <Background color="hsl(var(--muted-foreground))" gap={20} size={1} />
                  <Controls />
                </ReactFlow>
              </div>
              {topic?.diagrams?.[activeDiagram]?.caption && (
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <p className="text-sm text-muted-foreground italic bg-background/80 backdrop-blur-sm inline-block px-4 py-2 rounded-full">
                    {topic.diagrams[activeDiagram].caption}
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Understanding Check */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="text-2xl font-bold mb-6">Understanding Check</h2>

          <div className="space-y-6">
            {topic?.quiz?.questions?.map((question, qIndex) => (
              <Card key={question.id} className="card-base" data-testid={`quiz-question-${qIndex}`}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">
                      {qIndex + 1}
                    </span>
                    {question.question}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {question.type === "mcq" && question.options ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {question.options.map((option, oIndex) => {
                        const isSelected = quizAnswers[question.id] === option;
                        const isCorrect = showResults && option === question.correct_answer;
                        const isWrong = showResults && isSelected && option !== question.correct_answer;

                        return (
                          <button
                            key={oIndex}
                            onClick={() => !showResults && handleQuizAnswer(question.id, option)}
                            className={`quiz-option text-left ${isSelected ? "quiz-option-selected" : ""} ${isCorrect ? "quiz-option-correct" : ""} ${isWrong ? "quiz-option-incorrect" : ""}`}
                            data-testid={`quiz-option-${qIndex}-${oIndex}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-xs">
                                {String.fromCharCode(65 + oIndex)}
                              </span>
                              <span>{option}</span>
                              {showResults && isCorrect && <CheckCircle2 className="w-4 h-4 text-green-400 ml-auto" />}
                              {showResults && isWrong && <XCircle className="w-4 h-4 text-red-400 ml-auto" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <Textarea
                      placeholder="Type your answer..."
                      value={quizAnswers[question.id] || ""}
                      onChange={(e) => handleQuizAnswer(question.id, e.target.value)}
                      className="bg-background border-border"
                      data-testid={`quiz-short-answer-${qIndex}`}
                    />
                  )}

                  {showResults && question.explanation && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="mt-4 p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20"
                    >
                      <p className="text-sm text-indigo-300">
                        <strong>Explanation:</strong> {question.explanation}
                      </p>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Teach Back */}
            <Card className="card-base border-cyan-500/20" data-testid="teach-back-section">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-3">
                  <Brain className="w-5 h-5 text-cyan-400" />
                  Teach-Back Challenge
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  {topic?.quiz?.teach_back_prompt || "Explain this concept in your own words in 3 sentences."}
                </p>
                <Textarea
                  placeholder="Your explanation..."
                  value={teachBack}
                  onChange={(e) => setTeachBack(e.target.value)}
                  className="bg-background border-border min-h-[120px]"
                  data-testid="teach-back-input"
                />
              </CardContent>
            </Card>

            {/* Check Results Button */}
            <div className="flex justify-center">
              <Button
                onClick={checkQuiz}
                disabled={showResults || Object.keys(quizAnswers).length === 0}
                className="rounded-full px-8 py-6 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white font-bold"
                data-testid="check-answers-btn"
              >
                {showResults ? "Results Shown" : "Check My Understanding"}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </motion.section>
      </main>
    </div>
  );
}

// Lens Card Component
function LensCard({ lens, name }) {
  const config = lensConfig[name] || lensConfig.Professional;
  const Icon = config.icon;

  if (!lens) {
    return (
      <Card className="card-base">
        <CardContent className="py-12 text-center text-muted-foreground">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          Loading perspective...
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <Card className={`card-base border ${config.bgColor}`}>
        <CardContent className="pt-6 space-y-6">
          <div>
            <h3 className={`text-lg font-semibold mb-2 flex items-center gap-2 ${config.color}`}>
              <Lightbulb className="w-5 h-5" />
              Definition
            </h3>
            <p className="text-foreground">{lens.definition}</p>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-green-400">
              <Target className="w-5 h-5" />
              Example
            </h3>
            <p className="text-foreground">{lens.example}</p>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              Common Confusion
            </h3>
            <p className="text-foreground">{lens.common_confusion}</p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// Synthesis Card Component  
function SynthesisCard({ synthesis }) {
  if (!synthesis) {
    return (
      <Card className="card-base">
        <CardContent className="py-12 text-center text-muted-foreground">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          Creating synthesis...
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bento-grid"
    >
      {/* What it is - Spans 2 columns */}
      <Card className="card-base md:col-span-2 border-emerald-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm uppercase tracking-widest text-emerald-400">What it is</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-lg">{synthesis.what_it_is}</p>
        </CardContent>
      </Card>

      {/* Memory Anchor */}
      <Card className="card-base border-amber-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm uppercase tracking-widest text-amber-400">Memory Anchor</CardTitle>
        </CardHeader>
        <CardContent>
          <p>{synthesis.memory_anchor}</p>
        </CardContent>
      </Card>

      {/* Why it matters */}
      <Card className="card-base border-blue-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm uppercase tracking-widest text-blue-400">Why it matters</CardTitle>
        </CardHeader>
        <CardContent>
          <p>{synthesis.why_it_matters}</p>
        </CardContent>
      </Card>

      {/* How it works - Spans 2 columns */}
      <Card className="card-base md:col-span-2 border-purple-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm uppercase tracking-widest text-purple-400">How it works</CardTitle>
        </CardHeader>
        <CardContent>
          <p>{synthesis.how_it_works}</p>
        </CardContent>
      </Card>

      {/* Common Mistakes */}
      <Card className="card-base md:col-span-2 border-red-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm uppercase tracking-widest text-red-400">Common Mistakes</CardTitle>
        </CardHeader>
        <CardContent>
          <p>{synthesis.common_mistakes}</p>
        </CardContent>
      </Card>

      {/* One-line Takeaway */}
      <Card className="card-base border-cyan-500/30 bg-cyan-500/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm uppercase tracking-widest text-cyan-400">Key Takeaway</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-semibold text-cyan-100">{synthesis.one_line_takeaway}</p>
        </CardContent>
      </Card>
    </motion.div>
  );
}
